import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Injector,
  HostBinding,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ComponentService } from '../../../services/component/component.service';
import { ComponentInputBase } from '../../../models/ComponentInputBase.model';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { UserService } from '../../../services/user/user.service';
import { createGroupCalendarEvent } from '../../../services/component-data-dest/data/add-event';
import { environment } from '../../../environment/environment';
import {
  GroupCalendarDayRow,
  GroupCalendarDragEventData,
  GroupCalendarDropCellData,
  GroupCalendarLocationOption,
  GroupCalendarMonthData,
  GroupCalendarSettings,
  GroupCalendarStatsLocationColumn,
  GroupCalendarTemplateOption,
  GroupCalendarTemplateStatRow,
} from '../duna-events-group-calendar/group-calendar-settings.interface';
import {
  extractLocationOptions,
  fetchEventTemplates,
  loadGroupCalendarSettingsFromOrg,
  LOCATIONS_PAGE_SIZE,
  buildEventLocationPatch,
  buildOrderedSelectedLocations,
  buildStatsLocationColumns,
  buildTemplateLocationStatsFromApi,
  readGroupCalendarSettings,
  resolveGroupIdForDate,
  resolveGroupIdsForDate,
  setRowUserGroupIds,
  shiftEventDatesToDay,
  getWeeklyPlanTemplateId,
} from '../duna-events-group-calendar/group-calendar-settings.utils';
import {
  DayCellActionDialogComponent,
  DAY_CELL_ACTION_DIALOG_CONFIG,
} from '../duna-events-group-calendar/day-cell-action-dialog.component';
import { GroupPickDialogComponent } from '../duna-events-group-calendar/group-pick-dialog.component';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import {
  GroupCalendarGridComponent,
  GroupCalendarDropEvent,
  GroupCalendarCellClickEvent,
  GroupCalendarChipClickEvent,
} from '../duna-events-group-calendar/group-calendar-grid.component';
import {
  GroupCalendarOrgConfig,
  filterCalendarLocationGuids,
  userCanEditGroupCalendar,
  userCanViewGroupCalendar,
  extractPathIdsFromEducationalPaths,
  resolveUserPathIdsFromUserInfo,
} from '../duna-events-group-calendar/group-calendar-access.utils';
import {
  GROUP_CALENDAR_VIEW_ROLES_KEY,
  GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY,
  GROUP_CALENDAR_EDIT_PATH_IDS_KEY,
  GROUP_CALENDAR_DEFAULT_VIEW_ROLES,
  GROUP_CALENDAR_ALWAYS_ON_ROLES,
} from '../duna-org-settings/duna-org-settings.component';
import { resolveLocationGuid } from '../../../services/component-data-source/data/groups/duna_user_groups_location';
import { locationHasGroupCalendarGroups } from '../duna-user-groups-location/user-groups-location-display.util';
import { fetchLocationUserGroupsWithSettings } from '../duna-user-groups-location/user-groups-location-fetch.util';

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'Marts',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'December',
];

@Component({
  selector: 'app-duna-user-groups-location-calendar',
  standalone: true,
  imports: [MatSnackBarModule, MatIconModule, GroupCalendarGridComponent],
  templateUrl: './duna-user-groups-location-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-user-groups-location-calendar.component.scss',
})
export class DunaUserGroupsLocationCalendarComponent
  extends ComponentInputBase
  implements OnInit, OnDestroy
{
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  monthData: GroupCalendarMonthData | null = null;
  templateStats: GroupCalendarTemplateStatRow[] = [];
  locationOptions: GroupCalendarLocationOption[] = [];
  templates: GroupCalendarTemplateOption[] = [];
  groups: { group_id: number; name: string }[] = [];
  groupsEnabled = false;
  isLoading = false;
  dayGroupDefaults: Record<string, number[]> = {};

  calendarVisible = false;
  accessChecked = false;
  orgConfig: GroupCalendarOrgConfig | null = null;
  userLocationGuids: string[] = [];
  userPathIds: number[] = [];

  private readonly destroy$ = new Subject<void>();
  private initialized = false;

  @HostBinding('style.display')
  get hostDisplay(): string {
    if (!this.accessChecked) return 'none';
    return this.calendarVisible ? 'block' : 'none';
  }

  constructor(
    protected override componentService: ComponentService,
    private dataSourceService: ComponentDataSourceService,
    private dataDestService: ComponentDataDestService,
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private injector: Injector
  ) {
    super(componentService);
    this.inputs = {
      ...this.inputs,
      tableTitle: { type: 'text', value: 'Gruppekalender' },
      tableDescription: { type: 'textarea', value: '' },
      calendarLocationGuids: { type: 'json', value: [] as string[] },
      showGroupColumn: { type: 'boolean', value: true },
      templateColors: { type: 'json', value: {} as Record<string, string> },
      weeklyPlanByYear: {
        type: 'json',
        value: {} as Record<
          string,
          Record<string, Record<string, number | null>>
        >,
      },
      weeklyPlanYear: { type: 'number', value: new Date().getFullYear() },
      groupRotationByYear: {
        type: 'json',
        value: {} as Record<string, number[]>,
      },
      groupRotationYear: { type: 'number', value: new Date().getFullYear() },
      weekRotationByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['weekRotationByYear'],
      },
      weekRotationYear: { type: 'number', value: new Date().getFullYear() },
      activeRotationModeByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['activeRotationModeByYear'],
      },
      settingsRotationTab: { type: 'text', value: 'group' as const },
      weekRotationWeeklyPlanByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['weekRotationWeeklyPlanByYear'],
      },
    };
  }

  ngOnInit(): void {
    void this.init();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.initialized) void this.refreshCalendar();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async waitForAuthReady(maxMs = 5000): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < maxMs) {
      if (this.dataSourceService.authService.getCurrentUser()) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  private async init(): Promise<void> {
    await this.waitForAuthReady();
    await this.loadOrgConfigAndCheckAccess();
    if (!this.calendarVisible) {
      this.accessChecked = true;
      this.cdr.markForCheck();
      return;
    }
    await this.loadGroupsEnabled();
    await this.loadPersistedSettings();
    // User locations must be resolved before filtering location names
    await this.loadUserLocations();
    await Promise.all([
      this.loadLocationNames(),
      this.loadTemplates(),
      this.loadGroups(),
      this.loadUserPathIds(),
    ]);
    await this.loadMonth();
    this.accessChecked = true;
    this.initialized = true;
    this.cdr.markForCheck();
  }

  private async refreshCalendar(): Promise<void> {
    await this.waitForAuthReady();
    await this.loadPersistedSettings();
    await this.loadUserLocations();
    await this.loadLocationNames();
    await this.loadMonth();
  }

  private async loadOrgConfigAndCheckAccess(): Promise<void> {
    try {
      const authService = this.dataSourceService.authService;
      const currentUser = authService.getCurrentUser() as Record<
        string,
        unknown
      > | null;
      const orgId = this.resolveOrgId(currentUser);
      if (orgId == null) return;

      await this.loadUserPathIds();

      const res = await firstValueFrom(
        this.dataSourceService.executeDataSource('org_settings', {
          organizationId: orgId,
        })!
      );
      const data = (res as { data?: Record<string, unknown> })?.data ?? {};

      const groupCalEnabled =
        data['group_calendar_enabled'] === true ||
        data['group_calendar_enabled'] === 'true' ||
        data['group_calendar_enabled'] === 1;
      if (!groupCalEnabled) return;

      const rawViewRoles = this.normalizeStringArray(
        data[GROUP_CALENDAR_VIEW_ROLES_KEY]
      );
      const viewRoles =
        rawViewRoles.length > 0
          ? rawViewRoles
          : [
              ...GROUP_CALENDAR_DEFAULT_VIEW_ROLES,
              ...GROUP_CALENDAR_ALWAYS_ON_ROLES,
            ];
      const showAll =
        data[GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY] === true ||
        data[GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY] === 'true';
      const editPathIds = this.normalizeNumberArray(
        data[GROUP_CALENDAR_EDIT_PATH_IDS_KEY]
      );

      this.orgConfig = {
        groupCalendarEnabled: true,
        showAllLocations: showAll,
        viewRoles,
        editPathIds,
      };

      const userRoles: string[] = authService.getUserRoles();
      const userInfo =
        (currentUser?.['user_info'] as Record<string, unknown>) ?? {};

      this.calendarVisible = userCanViewGroupCalendar({
        userRoles,
        userInfo,
        viewRoles,
        editPathIds,
        userPathIds: this.userPathIds,
      });
      if (!this.calendarVisible) {
        return;
      }

      const locationGuid = resolveLocationGuid(this.router);
      if (locationGuid) {
        const { hideEmptyAtLocation, groups } =
          await fetchLocationUserGroupsWithSettings(
            this.dataSourceService,
            locationGuid
          );
        if (
          !locationHasGroupCalendarGroups(groups, hideEmptyAtLocation)
        ) {
          this.calendarVisible = false;
        }
      }
    } catch {
      this.calendarVisible = false;
    }
  }

  private resolveOrgId(
    user: Record<string, unknown> | null | undefined
  ): number | null {
    if (!user) return null;
    const orgId =
      user['organizations_id'] ??
      (user['user_info'] as Record<string, unknown> | undefined)?.[
        'organizations_id'
      ];
    if (orgId == null) return null;
    const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
    return Number.isFinite(n) ? n : null;
  }

  private normalizeStringArray(value: unknown): string[] {
    if (Array.isArray(value))
      return value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    return [];
  }

  private normalizeNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        const n = typeof item === 'number' ? item : parseInt(String(item), 10);
        return Number.isFinite(n) ? n : null;
      })
      .filter((n): n is number => n !== null);
  }

  private async loadGroupsEnabled(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('duna_groups_enabled', {})!
      );
      this.groupsEnabled = (result as { visible?: boolean })?.visible === true;
    } catch {
      this.groupsEnabled = false;
    }
  }

  private async loadPersistedSettings(): Promise<void> {
    const orgSettings = await loadGroupCalendarSettingsFromOrg(
      this.dataSourceService,
      this.viewYear
    );
    if (orgSettings) {
      this.applySettings(orgSettings);
    }
  }

  private applySettings(settings: GroupCalendarSettings): void {
    this.setInput('calendarLocationGuids', settings.calendarLocationGuids);
    this.setInput('showGroupColumn', settings.showGroupColumn);
    this.setInput('templateColors', settings.templateColors);
    this.setInput('weeklyPlanByYear', settings.weeklyPlanByYear);
    this.setInput('weeklyPlanYear', settings.weeklyPlanYear);
    this.setInput('groupRotationByYear', settings.groupRotationByYear);
    this.setInput('groupRotationYear', settings.groupRotationYear);
    this.setInput('weekRotationByYear', settings.weekRotationByYear);
    this.setInput('weekRotationYear', settings.weekRotationYear);
    this.setInput('activeRotationModeByYear', settings.activeRotationModeByYear);
    this.setInput('settingsRotationTab', settings.settingsRotationTab);
    this.setInput(
      'weekRotationWeeklyPlanByYear',
      settings.weekRotationWeeklyPlanByYear
    );
    this.dayGroupDefaults = Object.fromEntries(
      Object.entries(settings.dayGroupByDate).map(([key, ids]) => [key, [...ids]])
    );
  }

  get settings(): GroupCalendarSettings {
    return {
      ...readGroupCalendarSettings((key) => this.getInput(key), this.viewYear),
      dayGroupByDate: Object.fromEntries(
        Object.entries(this.dayGroupDefaults).map(([key, ids]) => [key, [...ids]])
      ),
    };
  }

  get showGroupColumn(): boolean {
    return this.groupsEnabled && this.settings.showGroupColumn;
  }

  get monthLabel(): string {
    return `${MONTH_NAMES[this.viewMonth]} ${this.viewYear}`;
  }

  get statsLocationColumns(): GroupCalendarStatsLocationColumn[] {
    return buildStatsLocationColumns(this.locationOptions);
  }

  private async loadLocationNames(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('locations', {
          filters: [
            { column: 'pageSize', value: LOCATIONS_PAGE_SIZE, operator: 'eq' },
            { column: 'pageIndex', value: 0, operator: 'eq' },
            // Only guid + name are used: skip per-user progress/validation on the server.
            { column: 'lightweight', value: 1, operator: 'eq' },
          ],
        })!
      );
      const items = result?.items ?? [];
      const allOptions = extractLocationOptions(
        Array.isArray(items) ? items : []
      );
      const allOrdered = buildOrderedSelectedLocations(
        this.settings.calendarLocationGuids,
        allOptions
      );

      // Apply location filter based on org config
      const showAll = this.orgConfig?.showAllLocations ?? false;
      const filteredGuids = filterCalendarLocationGuids(
        this.settings.calendarLocationGuids,
        this.userLocationGuids,
        showAll
      );
      this.locationOptions = allOrdered.filter((loc) =>
        filteredGuids.includes(loc.guid)
      );
    } catch {
      this.locationOptions = [];
    }
  }

  private async loadTemplates(): Promise<void> {
    try {
      this.templates = await fetchEventTemplates(this.dataSourceService);
    } catch {
      this.templates = [];
    }
  }

  private async loadGroups(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('duna_user_groups', {})!
      );
      const items = result?.items ?? [];
      this.groups = (Array.isArray(items) ? items : []).map(
        (g: { group_id: number; name: string }) => ({
          group_id: g.group_id,
          name: g.name,
        })
      );
    } catch {
      this.groups = [];
    }
  }

  private async loadUserLocations(): Promise<void> {
    try {
      const userService = this.injector.get(UserService);
      const locations = await firstValueFrom(
        userService.getUserLocationsFromApi()
      );
      this.userLocationGuids = (locations ?? [])
        .map((loc: { guid: string }) => loc.guid)
        .filter(Boolean);
    } catch {
      this.userLocationGuids = [];
    }
  }

  private async loadUserPathIds(): Promise<void> {
    try {
      const authService = this.dataSourceService.authService;
      const currentUser = authService.getCurrentUser() as Record<
        string,
        unknown
      > | null;
      const userInfo =
        (currentUser?.['user_info'] as Record<string, unknown>) ?? {};
      const pathIdsFromToken = resolveUserPathIdsFromUserInfo(userInfo);
      if (pathIdsFromToken !== null) {
        this.userPathIds = pathIdsFromToken;
        return;
      }
      const userService = this.injector.get(UserService);
      const paths = await firstValueFrom(userService.getEducationalPaths());
      this.userPathIds = extractPathIdsFromEducationalPaths(paths ?? []);
    } catch {
      this.userPathIds = [];
    }
  }

  async loadMonth(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      // Use only the visible (filtered) location guids, not all org locations
      const visibleGuids = this.locationOptions.map((l) => l.guid);
      const baseQueryParams = {
        year: this.viewYear,
        month: this.viewMonth,
        calendarLocationGuids:
          visibleGuids.length > 0
            ? visibleGuids
            : this.settings.calendarLocationGuids,
      };
      const [monthResult, statsResult] = await Promise.all([
        firstValueFrom(
          this.dataSourceService.executeDataSource('group_calendar_events', {
            queryParams: baseQueryParams,
          })!
        ),
        firstValueFrom(
          this.dataSourceService.executeDataSource('group_calendar_stats', {
            queryParams: baseQueryParams,
          })!
        ),
      ]);
      this.monthData = monthResult as GroupCalendarMonthData;
      this.templateStats = buildTemplateLocationStatsFromApi(
        statsResult.items ?? [],
        this.statsLocationColumns,
        this.templates,
        this.settings.templateColors
      );
      this.applyDayGroupDefaults();
    } catch {
      this.monthData = null;
      this.templateStats = [];
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private applyDayGroupDefaults(): void {
    if (!this.monthData) return;
    for (const row of this.monthData.days) {
      const resolved = resolveGroupIdsForDate(this.settings, row.dateKey);
      if (resolved.length > 0) {
        setRowUserGroupIds(row, resolved);
        row.user_group_name =
          this.groups.find((gr) => gr.group_id === resolved[0])?.name ?? null;
      } else if (!row.user_group_ids) {
        row.user_group_ids = [];
      }
    }
  }

  resolveGroupIds(row: GroupCalendarDayRow): number[] {
    const fromSettings = resolveGroupIdsForDate(this.settings, row.dateKey);
    if (fromSettings.length > 0) return fromSettings;
    if (row.user_group_ids?.length) return [...row.user_group_ids];
    if (row.user_group_id != null) return [row.user_group_id];
    return [];
  }

  resolveGroupId(row: GroupCalendarDayRow): number | null {
    const ids = this.resolveGroupIds(row);
    return ids.length > 0 ? ids[0] : null;
  }

  private async pickGroupFromList(
    title: string,
    message: string,
    groupIds: number[]
  ): Promise<number | null> {
    const options = groupIds
      .map((id) => this.groups.find((g) => g.group_id === id))
      .filter((g): g is { group_id: number; name: string } => g != null);
    if (options.length === 0) return null;
    if (options.length === 1) return options[0].group_id;
    return firstValueFrom(
      this.dialog
        .open(GroupPickDialogComponent, {
          width: '420px',
          data: { title, message, groups: options },
        })
        .afterClosed()
    );
  }

  private async resolveTargetGroupIdForMove(
    source: GroupCalendarDragEventData,
    targetRow: GroupCalendarDayRow
  ): Promise<number | null> {
    const targetGroups = this.resolveGroupIds(targetRow);
    const sourceGroupId = source.event.user_group_id ?? null;
    if (sourceGroupId != null && targetGroups.includes(sourceGroupId)) {
      return sourceGroupId;
    }
    if (targetGroups.length === 1) return targetGroups[0];
    if (targetGroups.length === 0) return null;
    return this.pickGroupFromList(
      'Vælg gruppe',
      'Dagen har flere grupper. Hvilken gruppe skal eventet tilknyttes?',
      targetGroups
    );
  }

  getLocationName(guid: string): string {
    return (
      this.locationOptions.find((l) => l.guid === guid)?.name ??
      guid.slice(0, 6)
    );
  }

  getDefaultTemplateIdForLocation(
    row: GroupCalendarDayRow,
    locationKey: string
  ): number | null {
    return getWeeklyPlanTemplateId(this.settings, locationKey, row.dateKey);
  }

  /**
   * Returns whether the current user can edit events in the given calendar column.
   */
  canEditCell = (locationKey: string): boolean => {
    if (!this.orgConfig) return false;
    const authService = this.dataSourceService.authService;
    const userRoles: string[] = authService.getUserRoles();
    return userCanEditGroupCalendar({
      userRoles,
      userPathIds: this.userPathIds,
      editPathIds: this.orgConfig.editPathIds,
      locationKey,
      userLocationGuids: this.userLocationGuids,
    });
  };

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else this.viewMonth--;
    void this.loadMonth();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else this.viewMonth++;
    void this.loadMonth();
  }

  // --- Grid output handlers ---

  onCellClickFromGrid(event: GroupCalendarCellClickEvent): void {
    void this.onCellClick(event.row, event.locationKey, event.locationLabel);
  }

  async onEventDroppedFromGrid(drop: GroupCalendarDropEvent): Promise<void> {
    const { source, target } = drop;
    const targetGroupId = await this.resolveTargetGroupIdForMove(
      source,
      target.row
    );
    if (targetGroupId == null) return;
    const sourceDateLabel = `${source.row.dayOfMonth}. ${
      MONTH_NAMES[this.viewMonth]
    }`;
    const targetDateLabel = `${target.row.dayOfMonth}. ${
      MONTH_NAMES[this.viewMonth]
    }`;
    const sourceLocationLabel =
      source.locationKey === 'other'
        ? 'Andet'
        : this.getLocationName(source.locationKey);
    const targetLocationLabel =
      target.locationKey === 'other'
        ? 'Andet'
        : this.getLocationName(target.locationKey);
    const details = [
      `Event: ${source.event.name}`,
      `Fra: ${sourceDateLabel} · ${sourceLocationLabel}`,
      `Til: ${targetDateLabel} · ${targetLocationLabel}`,
    ];
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmResetDialogComponent, {
          width: '520px',
          data: {
            title: 'Flyt event?',
            message:
              'Er du sikker på, at du vil flytte dette event til den nye dag?',
            details,
            confirmText: 'Ja, flyt event',
            cancelText: 'Annuller',
            confirmColor: 'primary',
          },
        })
        .afterClosed()
    );
    if (!confirmed) return;
    try {
      await this.moveEventToCell(source, target, targetGroupId);
      this.snackBar.open('Event flyttet', 'Luk', { duration: 3000 });
      await this.loadMonth();
    } catch {
      this.snackBar.open('Kunne ikke flytte event', 'Luk', { duration: 4000 });
    }
  }

  onChipClickFromGrid(event: GroupCalendarChipClickEvent): void {
    const { mouseEvent, row, locationKey, locationLabel } = event;
    const cell = row.cells[locationKey];
    if (cell && (mouseEvent.ctrlKey || mouseEvent.metaKey)) {
      window.open(
        `${window.location.origin}/edit-event/${cell.guid}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    void this.onCellClick(row, locationKey, locationLabel);
  }

  async onCellClick(
    row: GroupCalendarDayRow,
    locationKey: string,
    locationLabel: string
  ): Promise<void> {
    const dayGroupIds = this.resolveGroupIds(row);
    if (this.showGroupColumn && dayGroupIds.length === 0) {
      this.snackBar.open(
        'Vælg mindst én gruppe for dagen inden du opretter events',
        'Luk',
        { duration: 4000 }
      );
      return;
    }

    const existing = row.cells[locationKey];
    const dateLabel = `${row.dayOfMonth}. ${MONTH_NAMES[this.viewMonth]} ${
      this.viewYear
    }`;

    const ref = this.dialog.open(DayCellActionDialogComponent, {
      ...DAY_CELL_ACTION_DIALOG_CONFIG,
      data: {
        dateLabel,
        locationLabel,
        dateKey: row.dateKey,
        locationKey,
        existingEvent: existing,
        templates: this.templates,
        templateColors: this.settings.templateColors,
        defaultTemplateId: this.getDefaultTemplateIdForLocation(
          row,
          locationKey
        ),
        dayGroupOptions: dayGroupIds
          .map((id) => this.groups.find((g) => g.group_id === id))
          .filter((g): g is { group_id: number; name: string } => g != null),
      },
    });

    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    if (result.action === 'remove' && existing) {
      await this.deleteEvent(existing.guid);
      this.snackBar.open('Event slettet', 'Luk', { duration: 3000 });
      await this.loadMonth();
      return;
    }

    if (result.action === 'create' && result.template) {
      if (existing) await this.deleteEvent(existing.guid);
      const date = new Date(row.dateKey);
      const groupId =
        result.groupId ??
        existing?.user_group_id ??
        (dayGroupIds.length === 1 ? dayGroupIds[0] : null);
      if (groupId == null) {
        this.snackBar.open('Vælg en gruppe for eventet', 'Luk', {
          duration: 4000,
        });
        return;
      }
      try {
        await createGroupCalendarEvent(
          this.dataDestService,
          this.dataSourceService,
          result.template.template_data,
          date,
          locationKey,
          groupId,
          result.template.template_id,
          result.formValues
        );
        this.snackBar.open('Event oprettet', 'Luk', { duration: 3000 });
        await this.loadMonth();
      } catch {
        this.snackBar.open('Kunne ikke oprette event', 'Luk', {
          duration: 4000,
        });
      }
    }
  }

  private async deleteEvent(guid: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${environment.apiUrl}/events/${guid}`, {
        event: { deleted: true, is_active: false },
      })
    );
  }

  private async patchEvent(
    guid: string,
    eventPatch: Record<string, unknown>
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${environment.apiUrl}/events/${guid}`, {
        event: eventPatch,
      })
    );
  }

  private async moveEventToCell(
    source: GroupCalendarDragEventData,
    target: GroupCalendarDropCellData,
    targetGroupId: number | null
  ): Promise<void> {
    const dates = shiftEventDatesToDay(
      source.event.start_date,
      source.event.end_date,
      target.row.dateKey
    );
    const locationChanged = source.locationKey !== target.locationKey;
    const patch: Record<string, unknown> = {
      ...dates,
      user_group_id: targetGroupId,
    };
    if (locationChanged)
      Object.assign(patch, buildEventLocationPatch(target.locationKey));
    await this.patchEvent(source.event.guid, patch);
  }
}
